/**
 * Flight School backend config hook — PWA port.
 *
 * iOS ref: client-native/src/features/flight-school/hooks/useFlightSchoolConfig.ts
 *
 * The backend can author module config that OVERRIDES the hardcoded content
 * (GET /public/flightschool/config — admin UI at /flight-school-config). We fetch
 * it and resolve published modules (sorted by order) into a `moduleMap`, mirroring
 * iOS. The hardcoded `lessons/*.ts` content stays the source-of-truth FALLBACK:
 * when there's no active published config the endpoint returns nothing, the map is
 * empty, and the flow hook (`moduleMap[id] || FALLBACK_MODULE_MAP[id]`) uses the
 * bundled lessons — so the common case is unchanged and a server-authored config
 * transparently overrides it.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getFlightSchoolModuleConfig } from "../lib/flightSchoolApi";
import type { Module } from "../lib/activityTypes";

export interface FlightSchoolConfigModule {
  id: string;
  title: string;
  subtitle: string;
  type: string;
  order: number;
  published: boolean;
  patterns: string[];
  activities: Module["activities"];
  words?: Record<string, unknown>;
}

export interface UseFlightSchoolConfigReturn {
  /** Resolved module map keyed by module id (empty → use hardcoded fallback).
   *  Consumers read `.activities` (compatible with the hardcoded `Module`) and
   *  the config-only `.words`; the value is the raw config module, not a `Module`. */
  moduleMap: Record<string, FlightSchoolConfigModule>;
  /** Published modules sorted by order. */
  modules: FlightSchoolConfigModule[];
  isLoading: boolean;
}

export function useFlightSchoolConfig(): UseFlightSchoolConfigReturn {
  const query = useQuery({
    queryKey: ["flightSchoolConfig"],
    queryFn: () => getFlightSchoolModuleConfig(),
    staleTime: 5 * 60 * 1000,
  });

  // `config` is the parsed JSONB object from the backend ({ modules: [...] }).
  const modules = useMemo<FlightSchoolConfigModule[]>(() => {
    const raw = query.data?.config as
      | { modules?: FlightSchoolConfigModule[] }
      | undefined;
    if (!raw?.modules?.length) return [];
    return raw.modules
      .filter((m) => m.published)
      .sort((a, b) => a.order - b.order);
  }, [query.data]);

  const moduleMap = useMemo(() => {
    const map: Record<string, FlightSchoolConfigModule> = {};
    for (const m of modules) {
      map[m.id] = m;
    }
    return map;
  }, [modules]);

  return { moduleMap, modules, isLoading: query.isPending };
}
